import { useEffect, useMemo, useRef } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useQueryClient
} from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { Team, Teams } from '@/api';
import { addOrUpdateTeamCache } from './company-cache';
import { FETCH_TEAMS_PAGINATED_QUERY_KEY } from './company-query-keys';

const PAGE_SIZE = 25;

export type FetchTeamsPaginatedQuery = {
    companyId: string;
    search: string;
    /** Only teams connected to an alarm receiving centre. */
    arcEnabled?: boolean;
    /** Only teams that require a PIN. */
    pinEnabled?: boolean;
    /**
     * Only teams the requesting user holds one of these roles in. `admin`
     * covers the inherited team-admin role of company owners/admins, so they
     * match every team in the company; `memberOnly` is a team entry that
     * carries no role at all, so an owner/admin never matches it.
     */
    myTeamRoles?: ('admin' | 'guardian' | 'memberOnly')[];
    sortBy?: 'name';
    sortOrder?: 'asc' | 'desc';
    /**
     * Defaults to true. Set false to hold off until the list is actually
     * needed.
     */
    enabled?: boolean;
};

export type FetchTeamsPaginatedResult = {
    teams: Team[];
    total: number;
    status: 'error' | 'success' | 'pending';
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    refetch: () => void;
};

export function useFetchTeamsPaginated(
    query: FetchTeamsPaginatedQuery
): FetchTeamsPaginatedResult {
    const {
        companyId,
        search,
        arcEnabled,
        pinEnabled,
        myTeamRoles,
        sortBy,
        sortOrder,
        enabled = true
    } = query;

    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });
    const queryClient = useQueryClient();

    const {
        data,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: [
            FETCH_TEAMS_PAGINATED_QUERY_KEY,
            {
                companyId,
                search,
                arcEnabled,
                pinEnabled,
                myTeamRoles,
                sortBy,
                sortOrder
            }
        ],
        queryFn: ({ pageParam }) => {
            return openApiRef.current.teams
                .getTeams({
                    limit: PAGE_SIZE,
                    offset: pageParam * PAGE_SIZE,
                    companyId,
                    search: search || undefined,
                    arcEnabled,
                    pinEnabled,
                    myTeamRoles:
                        myTeamRoles && myTeamRoles.length > 0
                            ? myTeamRoles
                            : undefined,
                    sortBy,
                    sortOrder
                })
                .then((response): Teams => {
                    const teams = response.data.data.teams;
                    teams.data.forEach((team) => {
                        addOrUpdateTeamCache(queryClient, team);
                    });
                    return teams;
                });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.hasMore ? allPages.length : undefined;
        },
        // Keep showing the previous rows while a new search/filter/sort
        // combination loads, instead of flashing empty.
        placeholderData: keepPreviousData,
        enabled
    });

    // Flatten pages, deduping by id in case a mutation shifts page boundaries
    // between fetches.
    const teams = useMemo(() => {
        const map = new Map<string, Team>();
        data?.pages.forEach((page) => {
            page.data.forEach((team) => {
                map.set(team.id, team);
            });
        });
        return [...map.values()];
    }, [data]);

    const total = useMemo(() => {
        const pages = data?.pages;
        return pages && pages.length > 0 ? pages[pages.length - 1].total : 0;
    }, [data]);

    return {
        teams,
        total,
        status,
        fetchNextPage: () => {
            void fetchNextPage();
        },
        isFetchingNextPage,
        hasNextPage,
        refetch: () => {
            void refetch();
        }
    };
}
